import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaSyncAlt,
  FaFileDownload,
  FaUsers,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./BreakdownTracker1.css";
import "./AddUser.css";

const normalizeString = (str) => {
  return typeof str === "string" ? str.trim().toLowerCase() : "";
};

const AddUser = () => {
  const [users, setUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const gridRef = useRef(null);
  const navigate = useNavigate();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(
          `${process.env.REACT_APP_BASE_API_URL}/users/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert("User deleted successfully!");
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user.");
      }
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        normalizeString(user.name).includes(searchQuery.toLowerCase()) ||
        normalizeString(user.username).includes(searchQuery.toLowerCase()) ||
        normalizeString(user.email).includes(searchQuery.toLowerCase()) ||
        normalizeString(user.userrole).includes(searchQuery.toLowerCase()) ||
        normalizeString(user.contractor_name).includes(searchQuery.toLowerCase());

      const matchesRole =
        !selectedRole ||
        normalizeString(user.userrole) === normalizeString(selectedRole);

      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, selectedRole]);

  const resetTableFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setSelectedRole("");
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  const handleExport = () => {
    if (filteredUsers.length === 0) return;
    const exportData = filteredUsers.map((u) => ({
      Name: u.name || "",
      Username: u.username || "",
      Email: u.email || "",
      Contractor: u.contractor_name || "AMLS",
      Role: (u.userrole || "").toUpperCase(),
      "Civil ID / Badge": u.badge || "",
      "Fleet No": u.fleetno || u.fleetNo || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Users_${date}.xlsx`);
  };

  const formatRoleName = (role) => {
    if (!role) return "N/A";
    const r = role.toLowerCase();
    if (r === "driver_supv") return "Driver Supv";
    if (r === "admin") return "Admin";
    if (r === "technician") return "Technician";
    if (r === "driver") return "Driver";
    if (r === "coo") return "COO";
    if (r === "jm") return "JM";
    if (r === "oc") return "OC";
    return role.toUpperCase();
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        sortable: true,
        filter: true,
        minWidth: 180,
        flex: 1.2,
        cellRenderer: (params) => {
          const val = params.value || "—";
          return (
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {val}
            </span>
          );
        },
      },
      {
        headerName: "Username",
        field: "username",
        sortable: true,
        filter: true,
        minWidth: 240,
        flex: 1.6,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              color: "#0f172a",
              background: "#f1f5f9",
              padding: "3px 8px",
              borderRadius: "6px",
              display: "inline-block",
              lineHeight: 1.2,
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Email",
        field: "email",
        sortable: true,
        filter: true,
        minWidth: 320,
        flex: 2.0,
        cellRenderer: (params) => (
          <span
            style={{
              fontFamily: "'Inter', sans-serif",
              color: "#334155",
              fontWeight: 400,
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Contractor",
        field: "contractor_name",
        sortable: true,
        filter: true,
        minWidth: 130,
        flex: 0.9,
        valueGetter: (params) => params.data?.contractor_name || "AMLS",
        cellRenderer: (params) => (
          <span style={{ fontFamily: "'Inter', sans-serif" }}>
            {params.value}
          </span>
        ),
      },
      {
        headerName: "Role",
        field: "userrole",
        sortable: true,
        filter: true,
        minWidth: 130,
        flex: 0.9,
        cellRenderer: (params) => {
          const role = params.value ? String(params.value).toLowerCase() : "user";
          const displayRole = formatRoleName(params.value);
          return <span className={`role-badge ${role}`}>{displayRole}</span>;
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        width: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: "right",
        cellRenderer: (params) => {
          const user = params.data;
          if (!user) return null;
          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                height: "100%",
              }}
            >
              <button
                className="user-action-btn edit"
                title="Edit User"
                onClick={() => navigate(`/add-user-form/${user.user_id}`)}
              >
                <FaEdit />
              </button>
              <button
                className="user-action-btn delete"
                title="Delete User"
                onClick={() => handleDeleteUser(user.user_id)}
              >
                <FaTrash />
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const hasData = filteredUsers.length > 0;

  return (
    <div className="user-management-root bt-container">
      {/* Header with Arrow Back Button */}
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            <button
              className="bt-back-btn"
              onClick={() => navigate("/main")}
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>User Management</h1>
            </div>
          </div>
          <div className="bt-header-actions">
            <div className="bt-filter-group">
              <FaFilter className="bt-filter-icon" />
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="bt-status-filter"
              >
                <option value="">All Roles</option>
                <option value="admin">ADMIN</option>
                <option value="driver">DRIVER</option>
                <option value="driver_supv">DRIVER SUPV</option>
                <option value="technician">TECHNICIAN</option>
                <option value="oc">OC</option>
                <option value="coo">COO</option>
                <option value="jm">JM</option>
                <option value="user">USER</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bt-main-content">
        {/* Action Bar */}
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button
              onClick={() => navigate("/add-user-form")}
              className="bt-primary-btn"
            >
              <FaPlus /> Add New User
            </button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="bt-search-input"
              />
            </div>
            <button
              className="bt-icon-btn"
              onClick={resetTableFilters}
              title="Reset table filters"
              aria-label="Reset table filters"
              type="button"
            >
              <FaSyncAlt />
            </button>
            <button
              className="bt-export-btn"
              onClick={handleExport}
              disabled={!hasData}
            >
              <FaFileDownload /> Export Excel
            </button>
          </div>
        </div>

        {/* AG Grid Table View matching Violation/Breakdown Tracker */}
        <div className="bt-table-section">
          <div className="bt-table-container">
            {hasData ? (
              <div className="ag-theme-alpine bt-ag-grid">
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredUsers}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowHeight={38}
                  headerHeight={40}
                  pagination={true}
                  paginationPageSize={25}
                  animateRows={true}
                />
              </div>
            ) : (
              <div className="bt-empty-state">
                <FaUsers className="bt-empty-icon" />
                <h3>No user records found</h3>
                <p>Try adjusting your search or role filter, or add new users</p>
                <button
                  onClick={() => navigate("/add-user-form")}
                  className="bt-primary-btn"
                >
                  <FaPlus /> Add First User
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
